"use client";

/**
 * P3-15 · Header search entry. Renders the trigger button (replacing the P2
 * placeholder) and owns the overlay's open state. Opens on click or the global
 * `/` and ⌘/Ctrl-K shortcuts (ignored while the user is typing in another
 * field), and restores focus to the button when the overlay closes.
 */
import * as React from "react";

import {
  SearchOverlay,
  type FeaturedCategory,
} from "~/components/storefront/search/search-overlay";
import { useSignedIn } from "~/components/auth/signed-in-provider";
import { useTranslator } from "~/i18n/provider";

function isTypingTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null;
  if (!node) return false;
  const tag = node.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    node.isContentEditable
  );
}

export function SearchTrigger({
  featuredCategories = [],
  historyEnabled = true,
}: {
  featuredCategories?: FeaturedCategory[];
  historyEnabled?: boolean;
}) {
  // P17A-06 · From the context rather than a prop from the header RSC, for the
  // same reason `WishlistHeart` reads it that way: it only picks which store
  // the search history lives in (localStorage for a guest, the account
  // otherwise), and the header can no longer call `auth()` to answer it. See
  // `signed-in-provider.tsx`.
  const signedIn = useSignedIn();
  const [open, setOpen] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const t = useTranslator();

  const openOverlay = React.useCallback(() => {
    setOpen(true);
  }, []);

  const closeOverlay = React.useCallback(() => {
    setOpen(false);
    // Return focus to the opener (§01 — Esc/close returns focus to the trigger).
    buttonRef.current?.focus();
  }, []);

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (open) return;
      const slash = e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey;
      const cmdK = (e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey);
      if ((slash && !isTypingTarget(e.target)) || cmdK) {
        e.preventDefault();
        openOverlay();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, openOverlay]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={t("common.search")}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={openOverlay}
        className="rounded-md p-2 text-fg hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] lg:flex lg:items-center lg:gap-2 lg:rounded-full lg:border lg:border-border lg:py-1.5 lg:pl-3 lg:pr-4"
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          className="h-5 w-5 lg:h-4 lg:w-4"
        >
          <circle cx="11" cy="11" r="6.5" />
          <path d="m20 20-3.8-3.8" />
        </svg>
        <span className="hidden text-sm text-fg-muted lg:inline">
          {t("common.search")}
        </span>
      </button>

      <SearchOverlay
        open={open}
        onClose={closeOverlay}
        featuredCategories={featuredCategories}
        historyEnabled={historyEnabled}
        signedIn={signedIn}
      />
    </>
  );
}
